/**
 * Application roles, shared between server and web. Roles live in
 * users.roles (TEXT[]). The set is open-by-design so new stations can
 * be added without code or schema changes; the constants here capture
 * the ones we wire up explicitly so spelling matches across the stack.
 *
 * Long-term mapping (per the user's design conversation):
 *   inductee     - Own data only (Phase 2 self-service)
 *   learner      - Own training only. The default role for an existing
 *                  employee who SSOs in without an inductee/onboarding
 *                  record. Self-service like inductee, but scoped to the
 *                  training portal rather than the onboarding /me view.
 *   reception    - Arrivals list, check-in flow
 *   station_*    - Their station's queue and kit issuance
 *   coordinator  - Global view, blockers, reports
 *   hr           - DBS / right-to-work / pre-employment checklist data
 *   onboarding   - Pre-start onboarding: dashboard, templates, documents,
 *                  approvals, and the captured PII (bank details, next of
 *                  kin, address). Distinct from 'hr' so "who can see the
 *                  sensitive onboarding data" is one explicit role check.
 *                  Spans both the onboarding side and the mobilisation side.
 *   manager      - Their reports' progress (Phase 2+)
 *   admin        - Tenant config, user role assignment
 *   super_admin  - Multi-mobilisation, dangerous deletions, audit access
 *
 * Strings are stable identifiers; do not rename casually.
 */

export const ROLES = {
  INDUCTEE: 'inductee',
  LEARNER: 'learner',
  RECEPTION: 'reception',
  STATION_IT: 'station_it',
  STATION_PPE: 'station_ppe',
  STATION_TOOLS: 'station_tools',
  STATION_FLEET: 'station_fleet',
  STATION_DBSHR: 'station_dbshr',
  COORDINATOR: 'coordinator',
  HR: 'hr',
  ONBOARDING: 'onboarding',
  MANAGER: 'manager',
  ADMIN: 'admin',
  SUPER_ADMIN: 'super_admin',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

export const ALL_ROLES: readonly Role[] = Object.values(ROLES);

/**
 * Roles that grant only self-service access to a user's own record and
 * never any staff surface (lists, cross-record reads, other people's
 * media). `inductee` reaches the onboarding /me view; `learner` reaches
 * the training portal. Everything else counts as staff.
 */
export const SELF_SERVICE_ROLES: readonly Role[] = [ROLES.INDUCTEE, ROLES.LEARNER];

const SELF_SERVICE_ROLE_SET = new Set<string>(SELF_SERVICE_ROLES);

/**
 * True if the role set includes any staff role, i.e. anything beyond the
 * self-service roles. A user with no roles at all is not staff. This is
 * the one place "what counts as staff" is decided, shared by the API
 * (requireStaff) and the web nav/guards so the answer never drifts.
 */
export function hasStaffRole(roles: readonly string[]): boolean {
  return roles.some((r) => !SELF_SERVICE_ROLE_SET.has(r));
}

/**
 * Roles that may review onboarding submissions and the files inductees
 * upload (forms, drafts, upload tasks). The on-the-day station roles are
 * deliberately excluded: a qualification cert is HR data, not station kit.
 * Shared by the admin-onboarding API guard and the web surfaces that link
 * to those files, so the two never drift.
 */
export const ONBOARDING_ACCESS_ROLES: readonly Role[] = [
  ROLES.ADMIN,
  ROLES.SUPER_ADMIN,
  ROLES.ONBOARDING,
  ROLES.HR,
];

export function hasOnboardingAccess(roles: readonly string[]): boolean {
  return roles.some((r) => (ONBOARDING_ACCESS_ROLES as readonly string[]).includes(r));
}

/**
 * Roles that see every mobilisation by default rather than only the
 * ones they have been ticked into (mobilisation_staff). These are the
 * system / oversight roles; on-the-day staff (reception, station_*, hr,
 * manager, coordinator) are scoped to their assigned mobilisations.
 *
 * Only these roles may also clear the session scope to the cross-
 * mobilisation "all" view.
 */
export function seesAllMobilisations(roles: readonly string[]): boolean {
  return roles.includes(ROLES.SUPER_ADMIN) || roles.includes(ROLES.ADMIN);
}
